import { CamelCasedPropertiesDeep } from "type-fest";

import { Nullable } from "@ctra/utils";

import { DairyCowEntity } from "../cows";
import { FarmEntity } from "../farms";
import { HerdGroupEntity } from "../herd-groups";
import { UserEntity } from "../user";
import { DataDescriptorEntity } from "../data-dictionary";
import { EventCategory, EventEntity } from "../events";

/**
 * Change on the farm
 */
export enum Change {
  feed = "feed",
  concentrates = "concentrates",
  bedding = "bedding",
  other = "other:"
}

/**
 * New-style change definition which is sent to the BE
 * with the tracker to be processed. The legacy `change` and
 * `otherChange` fields got updated with the new ones.
 */
export interface ChangeDefinition {
  /**
   * Legacy fields
   */
  change: Change;
  otherChange?: string;
  /**
   * New fields
   */
  source?: EventEntity["source"];
  category?: EventCategory;
  subCategory?: string;
  annotationId?: EventEntity["id"];
}

/**
 * Effect to observe on the farm. This is a legacy value which
 * got replaced by the data descriptor, but for backward dependency
 * we keep them. Each compatible data descriptor has a mapping to
 * one of this values.
 */
export enum Effect {
  eating = "eating",
  laying = "laying",
  ruminating = "ruminating",
  walking = "walking",
  milkYield = "milkYield",
  standing = "standing",
  milkVisits = "milkVisits",
  conductivity = "conductivity",
  scc = "scc"
}

/**
 * Expected direction of change in the behaviour/KPI
 */
export enum Direction {
  increase = "increase",
  decrease = "decrease",
  stagnate = "stagnate"
}

/**
 * New style effect definition, where the single `effect` field
 * is now update with other properties related to the
 * data descriptor.
 */
export interface EffectDefinition {
  /**
   * Legacy, null if the data descriptor does not map to a legacy effect
   */
  effect: Nullable<Effect>;
  /**
   * New fields
   */
  dataDescriptorID: DataDescriptorEntity["id"];
  direction?: Direction;
  date?: string;
}

/**
 * Ask Ida tracker scope
 */
export enum Subject {
  cow = "cow",
  farm = "farm",
  herdGroup = "herd group"
}

/**
 * Metric as present in the back-end structure
 */
export type Metric =
  | "Eating"
  | "Walking"
  | "Standing"
  | "Laying"
  | "Ruminating"
  | "MilkYield"
  | "MilkVisits"
  | "Conductivity"
  | "SCC"
  | "Idle";

/**
 * Ask Ida tracker definition as it looks like after normalisation
 */
interface TrackerDefinition {
  changes: Array<ChangeDefinition>;
  effects: Array<EffectDefinition>;
  subjectType: Subject;
  period: boolean;
  startDate: string;
  finalDate: string;
  herdGroup?: HerdGroupEntity["id"];
  cow?: DairyCowEntity["id"];
  startEpoch: number;
  endEpoch: number;
  finalEpoch: number;
  createdEpoch: number;
  viewedIntermediateResults: Array<UserEntity["id"]>;
  viewedFinalResults: Array<UserEntity["id"]>;
}

export interface TrackerValues {
  ValueBefore: number;
  ValueAfter: number;
  ExpectedBefore?: number;
  ExpectedAfter?: number;
  ValueChangePercentage: number;
  Significant: boolean;
  ConfidenceLevel: number;
  LastSignificantChangeEpoch: number;
}

interface TrackerResultSource {
  Results: Record<Metric, TrackerValues>;
  Processed: boolean;
  Errors: Array<string>;
  UpdatedEpoch: number;
}

export type TrackerResult = CamelCasedPropertiesDeep<TrackerResultSource>;

export interface TrackerSource {
  IdaQnAID: number;
  farmID: number;
  active: boolean;
  question: Nullable<string>; // JSON string
  answer: Nullable<string>; // JSON string
}

export interface TrackerEntity {
  id: number;
  farm: FarmEntity["id"];
  active: boolean;
  question: TrackerDefinition;
  answer: TrackerResult;
}

export type TrackerSourceList = Record<TrackerSource["IdaQnAID"], TrackerSource>;
export type TrackerList = Record<TrackerEntity["id"], TrackerEntity>;

export type AskIdaState = {
  entities: TrackerList;
  collections: Record<string, Array<TrackerEntity["id"]>>;
};

export interface CreateTrackerFormValues {
  effects: Array<EffectDefinition>;
}

export interface CreateTrackerServerValues {
  period: boolean;
  subjectType: Subject;
  changes: Array<ChangeDefinition>;
  effects: Array<Omit<EffectDefinition, "date">>;
  herdGroup?: {
    HerdGroupID: HerdGroupEntity["id"];
  };
  startDate: string;
  finalDate: string;
  endDate: string;
  startEpoch: number;
  endEpoch: number;
  finalEpoch: number;
  createdEpoch: number;
}
